import { useState } from "react";
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from "react-native";

import type { UiTheme } from "../theme";
import { Icon } from "../Icon/Icon";

// A labelled SELECT (combo) choice for Settings — the dropdown sibling of
// SettingsPillChoice, for a choice with a growing / longer option list where a
// pill row would wrap awkwardly (e.g. the theme palette, which will grow with
// user-imported themes). Same prop shape as SettingsPillChoice so a mount can
// swap one for the other. The interaction lives here once; mobile + the web
// console both render it. Data orchestration stays in the mount.
//
// a11y: the field + every option is ≥44pt; the current option carries a "✓ "
// glyph + accessibilityState.selected (never colour alone); a full-bleed
// backdrop dismisses on outside tap.

export type SelectOption<T extends string> = { value: T; label: string };

export type SettingsSelectChoiceProps<T extends string> = {
  theme: UiTheme;
  label: string;
  help?: string;
  options: SelectOption<T>[];
  value: T | null;
  onPick: (value: T) => void;
  saving?: boolean;
  error?: string | null;
  disabled?: boolean;
  /** Placeholder shown when value doesn't match any option. */
  placeholder?: string;
};

export function SettingsSelectChoice<T extends string>({
  theme,
  label,
  help,
  options,
  value,
  onPick,
  saving = false,
  error = null,
  disabled = false,
  placeholder = "—",
}: SettingsSelectChoiceProps<T>) {
  const styles = makeStyles(theme);
  const [open, setOpen] = useState(false);
  const blocked = saving || disabled;
  const current = options.find((o) => o.value === value);

  return (
    // When open, lift the whole card above the following rows so the dropdown
    // paints OVER the next widget instead of being hidden behind it (web:
    // sibling paint-order; a plain zIndex on the inner menu isn't enough).
    <View style={[styles.card, open && styles.cardOpen]}>
      <Text style={styles.label}>{label}</Text>
      {help ? <Text style={styles.help}>{help}</Text> : null}
      {error ? <Text style={styles.error}>{error}</Text> : null}

      <View style={styles.fieldWrap}>
        <Pressable
          accessibilityRole="button"
          accessibilityState={{ expanded: open, disabled: blocked }}
          accessibilityLabel={label}
          accessibilityValue={{ text: current?.label ?? placeholder }}
          disabled={blocked}
          onPress={() => setOpen((v) => !v)}
          hitSlop={8}
          style={({ pressed }) => [
            styles.field,
            pressed && styles.fieldPressed,
            blocked && styles.fieldDisabled,
          ]}
        >
          <Text style={styles.fieldValue} numberOfLines={1}>
            {current?.label ?? placeholder}
          </Text>
          {saving ? (
            <ActivityIndicator color={theme.palette.ink2} />
          ) : (
            <Icon
              name={open ? "chevron-up" : "chevron-down"}
              size={theme.typography.sizes.md}
              color={theme.palette.ink2}
            />
          )}
        </Pressable>

        {open ? (
          <>
            <Pressable
              accessibilityLabel="Dismiss"
              onPress={() => setOpen(false)}
              style={styles.backdrop}
            />
            <View style={styles.menu}>
              {options.map((opt) => {
                const active = opt.value === value;
                return (
                  <Pressable
                    key={opt.value}
                    accessibilityRole="button"
                    accessibilityState={{ selected: active }}
                    onPress={() => {
                      setOpen(false);
                      if (!active) onPick(opt.value);
                    }}
                    style={({ pressed }) => [
                      styles.menuItem,
                      pressed && styles.menuItemPressed,
                    ]}
                  >
                    <Text
                      style={[styles.menuItemText, active && styles.menuItemActive]}
                      numberOfLines={1}
                    >
                      {active ? "✓ " : ""}
                      {opt.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </>
        ) : null}
      </View>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    card: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
      gap: s.s2,
      // relative + a resting zIndex so the open elevation has a stacking context.
      position: "relative",
      zIndex: 1,
    },
    // Open: sits above sibling rows so the absolute dropdown isn't covered.
    cardOpen: { zIndex: 100, elevation: 24 },
    label: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: "700",
      letterSpacing: 0.2,
    },
    help: { color: p.ink3, fontSize: t.sizes.sm },
    error: { color: p.danger, fontSize: t.sizes.base },
    // position:relative so the dropdown + its dismiss backdrop anchor here.
    fieldWrap: { position: "relative", marginTop: s.s2 },
    field: {
      minHeight: 36,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: s.s3,
      paddingHorizontal: s.s4,
      borderRadius: r.r3,
      borderWidth: 1,
      borderColor: p.borderStrong,
      backgroundColor: p.surface2,
    },
    fieldPressed: { opacity: 0.7 },
    fieldDisabled: { opacity: 0.5 },
    fieldValue: {
      flex: 1,
      minWidth: 0,
      color: p.ink,
      fontSize: t.sizes.base,
      fontWeight: "600",
    },
    backdrop: {
      position: "absolute",
      top: -2000,
      left: -2000,
      right: -2000,
      bottom: -2000,
      zIndex: 10,
    },
    menu: {
      position: "absolute",
      top: 48,
      left: 0,
      right: 0,
      backgroundColor: p.surface,
      borderRadius: r.r3,
      borderWidth: 1,
      borderColor: p.borderStrong,
      paddingVertical: s.s1,
      zIndex: 11,
      elevation: 8,
    },
    menuItem: {
      minHeight: 44,
      paddingHorizontal: s.s4,
      justifyContent: "center",
    },
    menuItemPressed: { opacity: 0.6 },
    menuItemText: { color: p.ink, fontSize: t.sizes.base, fontWeight: "600" },
    // Selected option: the "✓ " glyph + bold weight carry state — NOT an orange
    // text colour (brand500 as text fails AA ~2.9:1 on surface, and brand is
    // reserved for the primary action / live-now, never static content).
    menuItemActive: { fontWeight: "800" },
  });
}
